import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, TextField } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import competitive_intellligent from "../images/competitiveIntelligence 1.svg";
import { useGlobalContext } from "../provider/context/userContext";
import { Base_Url } from '../component/Services/ApiFile';

const theme = createTheme();

// Password requirement component
const PasswordRequirement = ({ meets, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
    <Box
      sx={{
        width: 12,
        height: 12,
        borderRadius: '50%',
        backgroundColor: meets ? '#4CAF50' : '#E0E0E0',
      }}
    />
    <span style={{ 
      color: meets ? '#4CAF50' : '#616161',
      fontSize: '0.8rem',
      fontFamily: 'satoshi'
    }}>
      {label}
    </span>
  </Box>
);

export default function PasswordResetConfirm() {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [visiblePassword, setVisiblePassword] = useState(false);
    const [visibleConfirmPassword, setVisibleConfirmPassword] = useState(false);
    const { uid, token } = useParams();
    const navigate = useNavigate();
    const globalContext = useGlobalContext();
    
    // Password validation checks
    const [passwordChecks, setPasswordChecks] = useState({
        minLength: false,
        hasUpperCase: false,
        hasLowerCase: false,
        hasNumber: false,
        hasSpecialChar: false,
    });
    
    // Check password requirements in real-time
    useEffect(() => {
        setPasswordChecks({
            minLength: password.length >= 8,
            hasUpperCase: /[A-Z]/.test(password),
            hasLowerCase: /[a-z]/.test(password),
            hasNumber: /\d/.test(password),
            hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
        });
    }, [password]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Check if all password requirements are met
        const allChecksPassed = Object.values(passwordChecks).every(Boolean);
        if (!allChecksPassed) {
            setError("Please meet all password requirements");
            return;
        }

        if (password !== confirmPassword) {
            setError("Passwords don't match");
            return;
        }

        try {
            const response = await fetch(Base_Url +`/auth/password-reset-confirm/${uid}/${token}/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    new_password: password,
                    re_new_password: confirmPassword
                }),
            });

            const data = await response.json();

            if (response.ok) {
                globalContext.Alert(data.status, data.message);
                navigate('/auth/login', { state: { success: "Password updated successfully!" } });
            } else {
                setError(data.detail || data.message || "Failed to reset password");
            }
        } catch (err) {
            setError("Network error occurred");
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <Box
                sx={{
                    height: "calc(80vh - 65px)",
                    overflow: "hidden",
                    maxWidth: "1000px",
                    marginX: "auto",
                    marginTop: "100px",
                    alignItems: "center",
                    padding: "50px",
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)"
                }}
            >
                <Grid container component='main' spacing={3}>
                    <CssBaseline />
                    <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{
                            padding: "20px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: "10px"
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px"
                            }}
                        >
                            <span
                                style={{
                                    color: "#000000",
                                    fontSize: "24px",
                                    fontWeight: "bold",
                                    lineHeight: "20px",
                                    fontFamily: "satoshi"
                                }}
                            >
                                Reset Your Password
                            </span>
                            <span
                                style={{
                                    color: "#000000",
                                    fontSize: "13px",
                                    fontFamily: "satoshi"
                                }}
                            >
                                Enter your new password below to reset your account.
                            </span>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px",
                                width: "100%"
                            }}
                        >
                            <label
                                style={{
                                    color: "#000000",
                                    fontSize: "16px",
                                    fontWeight: "bold",
                                    fontFamily: "satoshi !important"
                                }}
                                htmlFor='password'
                            >
                                New Password
                            </label>
                            <div
                                style={{
                                    backgroundColor: "#F2F2F2",
                                    padding: "8px 20px",
                                    borderRadius: "5px",
                                    width: "100%",
                                    color: "#000000",
                                    fontSize: "14px",
                                    border: password ? `1px solid ${Object.values(passwordChecks).every(Boolean) ? '#4CAF50' : '#FF9800'}` : '1px solid #F2F2F2',
                                    outline: "none",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    fontFamily: "satoshi !important"
                                }}
                            >
                                <input
                                    type={visiblePassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder='Enter new password'
                                    style={{
                                        border: "none",
                                        backgroundColor: "transparent",
                                        outline: "none",
                                        fontSize: "14px",
                                        fontFamily: "satoshi !important",
                                        width: "100%"
                                    }}
                                />
                                {visiblePassword ? (
                                    <VisibilityOffIcon
                                        style={{ color: "#52525B", cursor: "pointer" }}
                                        onClick={() => setVisiblePassword(!visiblePassword)}
                                    />
                                ) : (
                                    <RemoveRedEyeIcon
                                        style={{ color: "#52525B", cursor: "pointer" }}
                                        onClick={() => setVisiblePassword(!visiblePassword)}
                                    />
                                )}
                            </div>
                            
                            {/* Password requirements checklist */}
                            <Box sx={{ mt: 1 }}>
                                <PasswordRequirement 
                                    meets={passwordChecks.minLength} 
                                    label="At least 8 characters" 
                                />
                                <PasswordRequirement 
                                    meets={passwordChecks.hasUpperCase} 
                                    label="At least one uppercase letter" 
                                />
                                <PasswordRequirement 
                                    meets={passwordChecks.hasLowerCase} 
                                    label="At least one lowercase letter" 
                                />
                                <PasswordRequirement 
                                    meets={passwordChecks.hasNumber} 
                                    label="At least one number" 
                                />
                                <PasswordRequirement 
                                    meets={passwordChecks.hasSpecialChar} 
                                    label="At least one special character" 
                                />
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px",
                                width: "100%"
                            }}
                        >
                            <label
                                style={{
                                    color: "#000000",
                                    fontSize: "16px",
                                    fontWeight: "bold",
                                    fontFamily: "satoshi !important"
                                }}
                                htmlFor='confirmPassword'
                            >
                                Confirm New Password
                            </label>
                            <div
                                style={{
                                    backgroundColor: "#F2F2F2",
                                    padding: "8px 20px",
                                    borderRadius: "5px",
                                    width: "100%",
                                    color: "#000000",
                                    fontSize: "14px",
                                    border: confirmPassword ? `1px solid ${password === confirmPassword ? '#4CAF50' : '#FF9800'}` : '1px solid #F2F2F2',
                                    outline: "none",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    fontFamily: "satoshi !important"
                                }}
                            >
                                <input
                                    type={visibleConfirmPassword ? "text" : "password"}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder='Confirm new password'
                                    style={{
                                        border: "none",
                                        backgroundColor: "transparent",
                                        outline: "none",
                                        fontSize: "14px",
                                        fontFamily: "satoshi !important",
                                        width: "100%"
                                    }}
                                />
                                {visibleConfirmPassword ? (
                                    <VisibilityOffIcon
                                        style={{ color: "#52525B", cursor: "pointer" }}
                                        onClick={() => setVisibleConfirmPassword(!visibleConfirmPassword)}
                                    />
                                ) : (
                                    <RemoveRedEyeIcon
                                        style={{ color: "#52525B", cursor: "pointer" }}
                                        onClick={() => setVisibleConfirmPassword(!visibleConfirmPassword)}
                                    />
                                )}
                            </div>
                            {confirmPassword && password !== confirmPassword && (
                                <span style={{ color: '#FF9800', fontSize: '0.8rem' }}>
                                    Passwords do not match
                                </span>
                            )}
                        </Box>

                        {error && (
                            <div style={{
                                color: "#ff0000",
                                fontSize: "14px",
                                fontFamily: "satoshi",
                                width: "100%",
                                textAlign: "center"
                            }}>
                                {error}
                            </div>
                        )}

                        <Button
                            variant=''
                            disabled={!Object.values(passwordChecks).every(Boolean) || password !== confirmPassword}
                            sx={{
                                width: "100%",
                                background: "#000000",
                                color: "white",
                                padding: "10px",
                                fontSize: "14px",
                                fontFamily: "satoshi !important",
                                "&:hover": {
                                    background: "#000000",
                                    color: "#ffffff"
                                },
                                "&:disabled": {
                                    background: "#E0E0E0",
                                    color: "#9E9E9E"
                                }
                            }}
                            onClick={handleSubmit}
                        >
                            Update Password
                        </Button>

                        <p
                            style={{
                                color: "#000000",
                                textAlign: "center",
                                width: "100%",
                                fontSize: "1rem",
                                fontFamily: "satoshi !important"
                            }}
                        >
                            <span>
                                Remember your password?{" "}
                                <span
                                    style={{
                                        fontWeight: "bold",
                                        fontSize: "18px",
                                        marginLeft: "5px",
                                        cursor: "pointer"
                                    }}
                                    onClick={() => navigate("/auth/login")}
                                >
                                    Sign In
                                </span>
                            </span>
                        </p>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={6}
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center"
                        }}
                    >
                        <img
                            src={competitive_intellligent}
                            alt='Competitive Intelligence'
                            style={{
                                objectFit: "contain",
                                width: "100%"
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>
        </ThemeProvider>
    );
}